import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserCustomization } from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import useModalityCompatibleModels from '~/components/ModelSelection/useModalityCompatibleModels';
import { Modality } from '~/domain/dicom/modality';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type ImageTranslationModelSelectionProps = {
    modelCustomizations?: UserCustomization<unknown>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    onSubmit: (model: PredictionModel, selectedCustomization?: string) => void;
};

export default function ImageTranslationModelSelection({
    availableModels = [],
    modelCustomizations = [],
    modality,
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: ImageTranslationModelSelectionProps) {
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();

    const {
        compatibleModels,
        emptyMessage,
        labelAdornment,
        isSelectedModelCompatible,
    } = useModalityCompatibleModels(
        availableModels,
        modality,
        selectedModel,
        setSelectedModel
    );

    const customizations = useMemo(() => {
        if (!selectedModel || !modelCustomizations) {
            return [];
        }
        const selectedModelCustomizations =
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            );
        return selectedModelCustomizations || [];
    }, [selectedModel, modelCustomizations]);

    const hasCustomizations = customizations.length > 0;

    return (
        <>
            <Box
                display="grid"
                gridTemplateRows="240px auto"
                gridTemplateColumns={hasCustomizations ? '60% 1fr' : '100%'}
                gap={hasCustomizations ? 1 : 0}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={compatibleModels}
                    selectedModel={selectedModel}
                    onSelectedModelChanged={setSelectedModel}
                    emptyMessage={emptyMessage}
                    labelAdornment={labelAdornment}
                />

                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        onSelectedCustomizationChanged={
                            setSelectedCustomization
                        }
                    />
                )}
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <Stack gap={0.5}>
                    {showPseudonymization && <PseudonymizeCheckbox />}
                    <Tooltip
                        title={
                            !selectedModel ? (
                                <FormattedMessage id="selectAModelToStart" />
                            ) : (
                                ''
                            )
                        }
                        placement="top"
                    >
                        {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                        <Box component="span">
                            <Button
                                onClick={() =>
                                    selectedModel &&
                                    onSubmit(
                                        selectedModel,
                                        selectedCustomization
                                    )
                                }
                                variant="contained"
                                endIcon={<AutoAwesome />}
                                disabled={
                                    !selectedModel ||
                                    disabled ||
                                    isSubmitting ||
                                    !isSelectedModelCompatible
                                }
                                sx={{ width: 160 }}
                            >
                                <FormattedMessage id="start" />
                            </Button>
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>
        </>
    );
}
